import * as React from 'react';
import { FormField, InputAdornment, InputSlider } from '@cian/ui-kit';
import { MortgageCalculator as MortgageCalculatorComponent } from '../../components/MortgageCalculator';
import { Labels, Summary } from '../../components/MortgageCalculator/parts';
import { IFskCalculator } from '../../types/specialProjects/fsk/calculator';
import { EMortgageCalculatorType, reducer } from './reducer';
import { getLoanPayment, initialCalculatorStateMapper } from './helpers';
import { creditTermOptions, downPaymentOptions } from './config';
import { CallbackButtonContainer } from '../CallbackButton';

type THandlerType = 'propertyCost' | 'downPayment' | 'creditTerm' | 'downPaymentLabels';

interface IMortgageCalculatorProps {
  config: IFskCalculator;
  logo?: string;
}

export function MortgageCalculator({ config, logo }: IMortgageCalculatorProps) {
  const [state, dispatch] = React.useReducer(reducer, initialCalculatorStateMapper(config));
  const { propertyCost, downPayment, creditTerm, rate } = config;

  const handleSliderChange = React.useCallback(
    (type: THandlerType) => (value: number) => {
      const handler: Record<THandlerType, () => void> = {
        propertyCost: () => dispatch({ type: EMortgageCalculatorType.PropertyCostChange, payload: value }),
        downPayment: () =>
          dispatch({
            type: EMortgageCalculatorType.DownPaymentChange,
            payload: value > state.propertyCost * 0.9 ? state.downPayment : value,
          }),
        downPaymentLabels: () =>
          dispatch({ type: EMortgageCalculatorType.DownPaymentChange, payload: state.propertyCost * (value / 100) }),
        creditTerm: () => dispatch({ type: EMortgageCalculatorType.CreditTermChange, payload: value }),
      };

      handler[type]();
    },
    [state.downPayment, state.propertyCost],
  );

  const loanPayment = React.useMemo(
    () => getLoanPayment({ term: state.creditTerm, rate: state.rate, loan: state.propertyCost - state.downPayment }),
    [state],
  );
  const requiredIncome = React.useMemo(() => Math.ceil(loanPayment / 0.6).toLocaleString('ru'), [loanPayment]);

  return (
    <MortgageCalculatorComponent
      propertyCost={
        <FormField label="Стоимость недвижимости">
          <InputSlider
            min={propertyCost.min}
            max={propertyCost.max}
            step={propertyCost.step}
            value={state.propertyCost}
            onChange={handleSliderChange('propertyCost')}
            rightAdornment={<InputAdornment>{propertyCost.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      downPayment={
        <FormField label="Первоначальный взнос" withoutMargin>
          <InputSlider
            min={downPayment.min}
            max={downPayment.max}
            step={downPayment.step}
            value={state.downPayment}
            onChange={handleSliderChange('downPayment')}
            rightAdornment={<InputAdornment>{downPayment.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      downPaymentLabels={<Labels options={downPaymentOptions} onClick={handleSliderChange('downPaymentLabels')} />}
      creditTerm={
        <FormField label="Срок кредита" withoutMargin>
          <InputSlider
            min={creditTerm.min}
            max={creditTerm.max}
            step={creditTerm.step}
            value={state.creditTerm}
            onChange={handleSliderChange('creditTerm')}
            rightAdornment={<InputAdornment>{creditTerm.rightAdornment}</InputAdornment>}
          />
        </FormField>
      }
      creditTermLabels={<Labels options={creditTermOptions} onClick={handleSliderChange('creditTerm')} />}
      summary={
        <Summary
          logo={logo}
          rateTitle={rate.title}
          rateValue={rate.label}
          tooltip={rate.tooltip}
          loanPayment={`${loanPayment.toLocaleString('ru')} ₽`}
          requiredIncome={`${requiredIncome} ₽`}
        />
      }
      callback={
        <CallbackButtonContainer
          buttonText="Расскажем про ипотеку ФСК"
          customTheme="fsk_fill_primary"
          fullWidth={true}
          place="mortgage"
          size="L"
          theme="fill_primary"
        />
      }
    />
  );
}
